<template>
  <div class="images-wrapper my-row">
    <!-- MAIN SLIDER -->
    <div ref="slider" class="keen-slider main-slider">
      <!-- LEFT ARROW -->
      <font-awesome-icon
        size="3x"
        :icon="['fas', 'angle-left']"
        @click="!isPrevDisabled && prev()"
        :class="{
          arrow: true,
          arrow__left: true,
          'arrow--disabled': isPrevDisabled,
        }"
      />

      <!-- RIGHT ARROW -->
      <font-awesome-icon
        size="3x"
        :icon="['fas', 'angle-right']"
        @click="!isNextDisabled && next()"
        :class="{
          arrow: true,
          arrow__right: true,
          'arrow--disabled': isNextDisabled,
        }"
      />

      <!-- IMAGES -->
      <img
        class="keen-slider__slide"
        v-for="image in images"
        :key="image"
        :src="computedPath(image)"
        alt=""
      />
    </div>

    <!-- THUMBNAILS -->
    <div ref="thumbnail" class="keen-slider thumbnail">
      <img
        class="keen-slider__slide"
        v-for="image in images"
        :key="'thumb-' + image"
        :src="computedPath(image)"
        alt=""
      />
    </div>
  </div>
</template>

<script>
import "keen-slider/keen-slider.min.css";
import KeenSlider from "keen-slider";

export default {
  name: "Slider",

  props: {
    images: {
      type: Array,
      required: true,
    },
    imageBasePath: String,
    productName: String,
    productVariant: String,
    colorName: String,
  },

  data() {
    return {
      slider: null,
      thumbnail: null,
      currentSlide: 0,
      maxSlide: 0,
    };
  },

  computed: {
    isPrevDisabled() {
      return this.currentSlide <= 0;
    },
    isNextDisabled() {
      return this.currentSlide >= this.maxSlide;
    },
  },

  methods: {
    computedPath(image) {
      if (this.colorName) {
        return `${this.imageBasePath}${this.productName}-${this.colorName}/${image}`;
      }
      if (this.productVariant) {
        return `${this.imageBasePath}${this.productName}-${this.productVariant}/${image}`;
      }
      return `${this.imageBasePath}${this.productName}/${image}`;
    },

    prev() {
      if (!this.slider || this.isPrevDisabled) return;
      this.slider.prev();
    },

    next() {
      if (!this.slider || this.isNextDisabled) return;
      this.slider.next();
    },
  },

  mounted() {
    // MAIN SLIDER
    this.slider = new KeenSlider(this.$refs.slider, {
      slides: { perView: 1 },
      initial: 0,
      created: (slider) => {
        this.currentSlide = slider.track.details.rel;
        this.maxSlide = slider.track.details.maxIdx;
      },
      slideChanged: (slider) => {
        this.currentSlide = slider.track.details.rel;
      },
    });

    // THUMBNAIL SLIDER
    this.thumbnail = new KeenSlider(
      this.$refs.thumbnail,
      {
        initial: 0,
        slides: {
          perView: 6,
          spacing: 10,
        },
        breakpoints: {
          "(min-width: 1px)": {
            slides: { perView: 3, spacing: 5 },
          },
          "(min-width: 576px)": {
            slides: { perView: 4, spacing: 10 },
          },
          "(min-width: 768px)": {
            slides: { perView: 5, spacing: 10 },
          },
          "(min-width: 992px)": {
            slides: { perView: 4, spacing: 10 },
          },
          "(min-width: 1200px)": {
            slides: { perView: 5, spacing: 10 },
          },
          "(min-width: 1600px)": {
            slides: { perView: 6, spacing: 10 },
          },
        },
      },
      [ThumbnailPlugin(this.slider)]
    );
  },

  beforeDestroy() {
    if (this.slider) this.slider.destroy();
    if (this.thumbnail) this.thumbnail.destroy();
  },
};

// PLUGIN FOR THUMBNAILS
function ThumbnailPlugin(main) {
  return (slider) => {
    function removeActive() {
      slider.slides.forEach((slide) => slide.classList.remove("active"));
    }

    function addActive(idx) {
      slider.slides[idx].classList.add("active");
    }

    function addClickEvents() {
      slider.slides.forEach((slide, idx) => {
        slide.addEventListener("click", () => {
          main.moveToIdx(idx);
        });
      });
    }

    slider.on("created", () => {
      addActive(slider.track.details.rel);
      addClickEvents();

      main.on("animationStarted", (mainInstance) => {
        removeActive();
        const next = mainInstance.animator.targetIdx || 0;
        addActive(main.track.absToRel(next));
        slider.moveToIdx(Math.min(slider.track.details.maxIdx, next));
      });
    });
  };
}
</script>

<!-- SCOPED STYLES -->
<style scoped lang="scss">
.main-slider {
  position: relative;
}

.arrow {
  backdrop-filter: contrast(0.2);
  cursor: pointer;
}

.arrow__left {
  left: 1.5rem;
}

.arrow__right {
  right: 1.5rem;
}

.arrow--disabled {
  opacity: 0.3;
  cursor: default;
  pointer-events: none;
}
</style>

<!-- GLOBAL STYLES -->
<style lang="scss">
.thumbnail .keen-slider__slide {
  object-fit: scale-down;
  padding: 0;
  margin-top: 10px;
  cursor: pointer;
}

.thumbnail .keen-slider__slide.active {
  border: 2px solid $yellow;
}

.keen-slider {
  min-height: fit-content;
}

.keen-slider:not([data-keen-slider-disabled]) .keen-slider__slide {
  height: auto;
}
</style>
